/**
 * R8 diverging-stacked rating exhibit — the PURE data layer.
 *
 * Everything the `diverging_stacked` card needs that is math, not React:
 *   - band boundaries (Bottom-2 | Neutral | Top-2 for odd scales; bottom-half |
 *     top-half with NO neutral band for even scales),
 *   - one diverging row from a per-item score distribution (counts, shares, mean,
 *     top-2-box) + the centered-track layout geometry,
 *   - the METRIC-AWARE row-stat formatter (means never get a % sign — QA #14),
 *   - client-side demographic segment-cut derivation from raw responses
 *     (per item × per cut: distribution → row, with directional / suppression
 *     thresholds), tolerant of BOTH response shapes: the item-keyed map
 *     (`survey_data.qs_main = { itemId: value }`, the python contract landing with
 *     the diverging_stacked rollout) and today's plain scalar (single-item only),
 *   - the narrow-scope significance contract (precomputed python tests only —
 *     never a second stats engine in TS).
 *
 * Survey-agnostic: keyed only on scale bounds / attribute keys, never on question
 * or option text. No React imports so every helper is unit-testable directly.
 */
import {
  divergingBands,
  divergingLayout,
  divergingRowFromDistribution,
  type DivergingBands,
  type DivergingLayout,
  type DivergingRow,
} from '@digital-twins/deck-ui/components/charts/diverging'

import { buildSegmentCandidates, responseInCut, type SegmentQuestion } from './quickStudyPure'
import { displayAttrValue, humanizeAttr, qualifiedCutDisplayLabel } from './audience-segments'

export { divergingBands, divergingLayout, divergingRowFromDistribution }
export type { DivergingBands, DivergingLayout, DivergingRow }

// ── Significance (python-precomputed; narrow scope per the architect decision) ──
export interface SignificanceEntry {
  comparison: 'item_vs_item' | 'segment_vs_everyone'
  a: string
  b: string
  metric: string
  p_value: number
  significant_95: boolean
  claim: string
}

/** The tested-claim strings to render in the card caption: page-vs-page tests that
 *  actually reached significance. Ad-hoc demographic cut rows NEVER get markers
 *  (policy: bases rarely support it; consistent with the prose gate). */
export function itemSignificanceClaims(entries: Array<SignificanceEntry> | undefined | null): string[] {
  return (entries ?? [])
    .filter((e) => e && e.comparison === 'item_vs_item' && e.significant_95 && (e.claim ?? '').trim())
    .map((e) => e.claim.trim())
}

// ── Band boundaries, diverging row + layout ──────────────────────────────────
// `divergingBands`, `divergingRowFromDistribution` and `divergingLayout` (and their
// types) are imported from deck-ui above and re-exported for existing callers of
// this module. This is the producer-rounded (half away from zero, with
// `bottom2Pct`) definition Python and deck-ui share; Quick Study previously
// carried its own copy without `bottom2Pct`, which the parity guard
// (`diverging-primitives-parity.test.ts`) caught as drift.

/** Distribution (per-value counts) from raw values — the client-side cut path. */
export function distributionFromValues(values: Array<number>, scaleMin: number, scaleMax: number): Record<number, number> {
  const dist: Record<number, number> = {}
  for (const raw of values) {
    const v = Math.round(raw)
    if (v < scaleMin || v > scaleMax) continue
    dist[v] = (dist[v] ?? 0) + 1
  }
  return dist
}

// ── Centered-track layout ────────────────────────────────────────────────────
// (`divergingLayout` and `DivergingLayout` are imported from deck-ui above.)

// ── Metric-aware row stats (QA #14: a mean NEVER gets a % sign) ──────────────
export function formatRowStats(top2Pct: number, mean: number): string {
  return `${Math.round(top2Pct)}% top-2 · avg ${mean.toFixed(1)}`
}

// ── Compare-segments caps + honesty thresholds ───────────────────────────────
/** Cut cap: 5 cuts on a single-item card, 3 when comparing 2+ items (worst case
 *  ~8 rows) — past the cap the picker disables further checkboxes. */
export function maxCutsFor(itemCount: number): number {
  return itemCount >= 2 ? 3 : 5
}

/** Floor pair lives in the shared aggregation lib (R4.1). Values unchanged (5 / 15). */
import { CUT_DIRECTIONAL_N, CUT_SUPPRESS_N } from '@digital-twins/deck-ui/lib/aggregation'

export { CUT_DIRECTIONAL_N, CUT_SUPPRESS_N }

// ── Raw-response attribution ─────────────────────────────────────────────────
// Minimal response shape (mirrors TwinResponse without importing the page module).
export type RatingResponse = {
  persona_id?: string
  survey_data: Record<string, any>
  attributes: Record<string, any>
  /** Authored "Audience segments" tags — bare-label cut ids match against these. */
  segment_labels?: string[] | null
}

/** Resolve the rating a response gave for ONE item.
 *  Shapes tolerated (graceful until the python item-keyed contract lands):
 *    - item-keyed map:    survey_data[q] = { itemId: 4 }
 *    - single-metric map: survey_data[q] = { itemId: { col: 4 } } (matrix shape)
 *    - plain scalar:      survey_data[q] = 4 — attributable only when the card has
 *      a single item (`acceptScalar`), since a pooled multi-item scalar carries no
 *      item identity.
 *  `colId` (Step 4B-ii, scorecard): a matrix cell holds ONE rating per DIMENSION column
 *  (`survey_data[q] = { itemId: { dimId: 4 } }`); pass the dimension's column id to read that
 *  specific dimension instead of collapsing the cell to its FIRST inner numeric. Omitted →
 *  the legacy first-numeric behaviour (single-metric grids), so every existing caller is
 *  byte-identical. */
export function ratingValueForItem(
  surveyData: Record<string, any> | undefined | null,
  questionName: string,
  itemId: string,
  acceptScalar: boolean,
  colId?: string,
): number | null {
  const v = surveyData?.[questionName]
  if (typeof v === 'number' && Number.isFinite(v)) return acceptScalar ? v : null
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const cell = v[itemId]
    if (typeof cell === 'number' && Number.isFinite(cell)) return cell
    if (cell && typeof cell === 'object' && !Array.isArray(cell)) {
      // A specific dimension column (scorecard) — never fall back to a sibling dimension.
      if (colId != null) {
        const dim = (cell as Record<string, any>)[colId]
        return typeof dim === 'number' && Number.isFinite(dim) ? dim : null
      }
      for (const inner of Object.values(cell)) {
        if (typeof inner === 'number' && Number.isFinite(inner)) return inner
      }
    }
  }
  return null
}

/** Can raw responses be attributed per item at all? Gates the Compare-segments
 *  picker: a stored multi-item run whose responses are plain pooled scalars cannot
 *  be cut client-side (no item identity), so the picker is hidden — no dead control. */
export function itemsAttributable(
  responses: Array<RatingResponse> | undefined | null,
  questionName: string,
  itemIds: Array<string>,
): boolean {
  if (!responses?.length || !itemIds.length) return false
  const acceptScalar = itemIds.length === 1
  for (const r of responses) {
    for (const id of itemIds) {
      if (ratingValueForItem(r?.survey_data, questionName, id, acceptScalar) != null) return true
    }
  }
  return false
}

// ── Segment-cut option groups + per-cut rows ─────────────────────────────────
export interface SegmentCutOption {
  /** `${attributeKey}::${rawValue}` — parseable back via parseCutId. */
  id: string
  label: string
  n: number
}
export interface SegmentCutGroup {
  groupLabel: string
  options: Array<SegmentCutOption>
}

const CUT_ID_SEP = '::'
export function cutId(attribute: string, value: string): string {
  return `${attribute}${CUT_ID_SEP}${value}`
}
export function parseCutId(id: string): { attribute: string; value: string } {
  const at = id.indexOf(CUT_ID_SEP)
  if (at < 0) return { attribute: id, value: '' }
  return { attribute: id.slice(0, at), value: id.slice(at + CUT_ID_SEP.length) }
}

/** Demographic option groups for the Compare-segments picker, derived from the
 *  run's own responses (buildSegmentCandidates owns which attributes qualify).
 *  Option `n` counts UNIQUE twins (monadic runs reuse the panel across items, so a
 *  raw row count would inflate by the item count). "Everyone" is deliberately NOT
 *  an option — it is the always-rendered anchor row.
 *
 *  Wave 2 note: authored "Audience segments" prepend as an extra group AHEAD of
 *  these — same SegmentCutGroup shape, no API change. */
export function buildDemographicCutGroups(
  responses: Array<RatingResponse>,
  audienceType?: string,
): Array<SegmentCutGroup> {
  const candidates: Array<SegmentQuestion> = buildSegmentCandidates(responses, audienceType)
  if (!candidates.length) return []
  // Unique-twin tally per attribute value. Fall back to the row index when a
  // response has no persona_id (legacy payloads) — over-counts, never under.
  const groups: Array<SegmentCutGroup> = []
  for (const cand of candidates) {
    const seen = new Map<string, Set<string>>()
    responses.forEach((r, idx) => {
      const raw = r?.attributes?.[cand.name]
      if (raw == null || typeof raw === 'object') return
      const val = String(raw).trim()
      if (!val) return
      if (!seen.has(val)) seen.set(val, new Set())
      seen.get(val)!.add(r.persona_id ? String(r.persona_id) : `#${idx}`)
    })
    const options = cand.choices
      .map((c) => ({ id: cutId(cand.name, c.value), label: c.text, n: seen.get(c.value)?.size ?? 0 }))
      .filter((o) => o.n > 0)
    if (options.length >= 2) groups.push({ groupLabel: cand.title, options })
  }
  return groups
}

export interface CutRow {
  cutId: string
  /** "Gender · Male"-style label (group context + translated value). */
  label: string
  n: number
  row: DivergingRow | null
  /** n < CUT_DIRECTIONAL_N → read as directional only. */
  directional: boolean
  /** n < CUT_SUPPRESS_N → excluded from the plot, named in the warning line. */
  suppressed: boolean
}

/** One item × one cut: filter raw responses to the cut, derive the distribution,
 *  and build the diverging row (null row when nothing usable landed). `colId` (Step 4B-ii)
 *  addresses a specific scorecard dimension column within each matrix cell; omitted → the
 *  single-metric read (first inner numeric). */
export function deriveCutRow(
  responses: Array<RatingResponse>,
  questionName: string,
  itemId: string,
  acceptScalar: boolean,
  selectedCutId: string,
  scaleMin: number,
  scaleMax: number,
  colId?: string,
): CutRow {
  const { attribute, value } = parseCutId(selectedCutId)
  const values: Array<number> = []
  for (const r of responses) {
    // Shared membership predicate: "attribute::value" tests `attributes`, a bare
    // authored label tests `segment_labels[]` — same semantics as every other slicer.
    if (!responseInCut(r, selectedCutId)) continue
    const v = ratingValueForItem(r.survey_data, questionName, itemId, acceptScalar, colId)
    if (v != null) values.push(v)
  }
  const dist = distributionFromValues(values, scaleMin, scaleMax)
  const row = divergingRowFromDistribution(dist, scaleMin, scaleMax)
  const n = row?.n ?? 0
  return {
    cutId: selectedCutId,
    // A demographic cut shows its translated value (attribute-prefixed when the bare
    // value alone is ambiguous — "Tech comfort: 1"); an authored cut its bare label.
    label: value ? qualifiedCutDisplayLabel(humanizeAttr(attribute), displayAttrValue(value)) : selectedCutId,
    n,
    row,
    directional: n > 0 && n < CUT_DIRECTIONAL_N,
    suppressed: n < CUT_SUPPRESS_N,
  }
}

// ── Scorecard cell recompute (Step 4B-ii: browser recompute, ZERO backend) ────
/** One scorecard cell's stats (item × dimension), recomputed CLIENT-SIDE from raw
 *  responses that are ALREADY sliced to the target cut (Everyone = the full set). */
export interface ScorecardCellStats {
  n: number
  mean: number
  top2Pct: number
  /** Per-value counts — the rounding-free PARITY target vs python's published
   *  monadic_scorecard[].items[].distribution. */
  distribution: Record<number, number>
}

/** Recompute one scorecard cell from raw responses (per item × per dimension). Reads
 *  `survey_data[qName][itemId][colId]` per twin — the SAME cell python aggregates in
 *  `_scorecard_dim_item_stats` — then buckets to the shared distribution and derives the
 *  mean / top-2-box off ONE math path (`divergingRowFromDistribution`). The caller slices
 *  `responses` to the cut first (via the Step-3 cut-id slicer), so this stays cut-agnostic.
 *  Rounding note: ratings are integers, so `distributionFromValues`' round is a no-op and
 *  the recomputed distribution matches python's `int(round(v))` counts EXACTLY; only the
 *  DISPLAY percentages can differ (banker's vs half-up), which is why parity asserts on the
 *  distribution, not the rounded stat. */
export function scorecardCellStats(
  responses: Array<RatingResponse>,
  questionName: string,
  itemId: string,
  colId: string,
  scaleMin: number,
  scaleMax: number,
): ScorecardCellStats {
  const values: Array<number> = []
  for (const r of responses) {
    const v = ratingValueForItem(r?.survey_data, questionName, itemId, false, colId)
    if (v != null) values.push(v)
  }
  const distribution = distributionFromValues(values, scaleMin, scaleMax)
  const row = divergingRowFromDistribution(distribution, scaleMin, scaleMax)
  return { n: row?.n ?? 0, mean: row?.mean ?? 0, top2Pct: row?.top2Pct ?? 0, distribution }
}
